// Libs
import findIndex from 'lodash/findIndex';
// Our stuff
import translations from '_shared_/lang/translations';
import localizations from '_shared_/lang/localizations.js';
import integrations from '_shared_/js/integrations/integrationsData.js';
import { dispatchAnimation } from '../../_shared_/js/utils/pageTransitionHelper';
import { aiIntegrationsAPI } from './communication/integrations';
// modules
import PdfPagedReaderScale from './store/modules/PdfPagedReaderScale';
import { isAndroid as detectAndroid, isApple as detectApple } from './utils/platform.js';

import { notesAPI } from './communication/notes';
import { navigationAPI, settingsAPI, uiAPI } from './communication';
import { Link } from '@readium/shared';
import {
    API_FONT_SIZE_BASE,
    API_FONT_SIZE_MAX,
    API_FONT_SIZE_MIN,
    FONT_SIZE_MAX,
    FONT_SIZE_MIN,
    createR2Locator,
} from './components/partials/Epub/Readium/readium2.js';
import { FONT_SIZE_DEFAULT } from './components/partials/Epub/Readium/readium2-styles.js';

// Environment variables
const POLPO_URL = import.meta.env.VITE_POLPO_URL;
const CONIGLIO_URL = import.meta.env.VITE_CONIGLIO_URL;
const ENVIRONMENT = import.meta.env.VITE_ENV;
const VOLPE_HOST = import.meta.env.VITE_VOLPE_HOST;

const DEFAULT_USER_SETTINGS = () => ({
    epub: { layout: null, zoom: null, fontFamily: null, lineHeight: null, margins: null },
    pdf: { layout: null },
    theme: null,
});

const BASE_THEMES = ['system', 'light', 'dark'];

// Sidebar panel offset constants (panel width + gap)
const SIDEBAR_NO_OFFSET = 0;
const SIDEBAR_PANEL_OFFSET = 380;
const SIDEBAR_PLUGIN_OFFSET = 432;

// Runtime validation for VOLPE_HOST
if (!VOLPE_HOST) {
    console.error('CRITICAL: VITE_VOLPE_HOST environment variable is not set!');
    throw new Error('VITE_VOLPE_HOST environment variable is required. This should be set during build time.');
}
if (!['farfalla', 'fenice'].includes(VOLPE_HOST)) {
    console.error(`CRITICAL: Invalid VITE_VOLPE_HOST value: "${VOLPE_HOST}"`);
    throw new Error(`Invalid VITE_VOLPE_HOST value: "${VOLPE_HOST}". Must be either "farfalla" or "fenice".`);
}

export default {
    // strict: process.env.VITE_ENV !== 'production',
    state: {
        volpeHost: VOLPE_HOST,
        plaPublicationManifestVersion: null,
        tenantId: null, // Tenant ID for X-Farfalla-Tenant-Id header
        contentEncryption: {
            ivSeed: {
                tenant_id: null,
                issue_id: null,
            },
        },
        coniglio: {
            url: CONIGLIO_URL,
            track_token: '',
        },
        // fileType: 'audio',
        fileType: 'pdf',
        // fileType: 'epub',
        embeddedMode: false,
        previewMode: false,
        customPreview: false,
        disableNext: false,
        environment: ENVIRONMENT,
        inIframe: false,
        embeddedInMobileApp: ['fenice', 'tartaruga'].includes(VOLPE_HOST),
        isLoadingProcessFinished: false,
        sessionExpired: false,
        polpoSessionInitiated: false,
        readerToken: null,
        anonymousSession: false,
        parentUrl: null,
        baseEndpoint: `${POLPO_URL}/api/v1`,
        lang: 'es',
        translations,
        localizations,
        title: '',
        isLoading: true,
        loadingSpinnerPosition: null,
        uiHidden: false,
        device: {
            width: 0,
            height: 0,
            orientation: 'portrait',
        },
        isBuyable: false,
        userIsGuest: false,
        isBuyableButtonText: '',
        exhibitionMode: {
            enabled: false,
            label: '',
        },

        // check if merge with `publication` obj
        issue: {
            title: '',
            tags: '',
            issue_url: '',
            lang: 'en',
            // This attribute should not be used in Tartaruga
            share_url: '',
            publication_date: '',
            publicationPlace: '',
            aboutThisPublication: '',
            descriptionRawText: '',
            externalId: '',
            twitterUser: '',
        },

        // check if delete
        sidebar: {
            opened: false,
        },

        reader: {
            layout: 'scroll',

            pagination: {
                ready: false,
                total: 1,
                current: 1,

                isFirstPage: false,
                isLastPage: false,
            },

            // check if delete
            data: {
                toc: [],
            },

            keyboardNavigationEnabled: true,
            pageViewConfirmDelayMs: null,
        },
        sharedArticle: {
            showed: false,
            id: '',
        },
        search: {
            requestQueryWordsHighlight: false,
            queryWords: [],
            clear: false,
        },
        // check merge into one object
        articles: [],
        currentPageArticles: [],
        smartZoom: {
            enabled: false,
            visible: false,
            fontSizeMultiplier: 1,
            currentArticleIndex: 0,
        },

        // check if merge with `issue` obj
        publication: {
            cover: null,
            table_of_contents: [],
            files_urls: [],
            files_info: [],
            url: null,
        },

        audiobook: {
            playlist: [],
            chapters: null,
            duration: null,
            currentTrackDurationInSeconds: null,
            speed: 1,
            track: {
                durationInSeconds: null,
            },
            trackPlayer: {
                isLoading: false,
                isPlaying: false,
            },
        },

        ui: {
            theme: 'system',
            baseTheme: 'system',
            themeSelected: 'light',
            themeOptions: [
                {
                    value: 'system',
                    icon: 'theme-system',
                    solidIcon: 'theme-system',
                    label: 'System',
                },
                { value: 'light', icon: 'sun', solidIcon: 'sun-solid', label: 'Light' },
                { value: 'dark', icon: 'moon', solidIcon: 'moon-solid', label: 'Dark' },
            ],
            headerbar: {
                offsetHeight: 0,
            },
            toolbar: {
                offsetHeight: 0,
            },
            thumbnails: {
                opened: false,
                enabled: true,
            },
            pagination: {
                current: 1,
            },
            sidebar: {
                opened: false,
            },
            features: {
                highlights: {
                    opened: false,
                },
                notes: {
                    opened: false,
                },
                bookmarks: {
                    opened: false,
                    enabled: false,
                },
            },
            search: {
                opened: false,
                enabled: false,
            },
            notebook: {
                opened: false,
            },
            tableOfContents: {
                opened: false,
                enabled: true,
            },
            listening: {
                opened: false,
                enabled: true,
                texToListen: '',
                pageToListen: '',
                uuidToListen: '',
                driver: 'native-tts',
            },
            extraVoices: {
                chinese: false,
            },
            selectionTool: {
                enabled: true,
                opened: false,
                position: {},
                panelSide: 'right',
                selectionTop: 0,
                contentLeft: 0,
                contentRight: 0,
            },
            rightSubmenu: {
                opened: false,
            },
            citationModal: {
                opened: false,
            },
            plugin: {
                opened: false,
                enabled: true,
                src: null,
                localizations: {
                    alt: 'Vito AI',
                },
            },
            settings: {
                opened: false,
                enabled: true,
                zoom: {
                    enabled: true,
                    select: {
                        enabled: true,
                        types: ['page-fit', 'page-width'],
                        isSelected: 'page-fit',
                        scale: 1,
                    },
                },
            },
            paginator: {
                enabled: true,
            },
            audiobook: {
                about: {
                    opened: false,
                },
                playlist: {
                    opened: false,
                },
            },
            socialShare: {
                opened: false,
                shareArticle: false,
                currentArticleId: null,
            },
            notesModal: {
                opened: false,
                selection: [],
            },
            confirmationModal: {
                opened: false,
                title: '',
                message: '',
                confirmLabel: '',
                cancelLabel: '',
                onConfirmAction: null,
                context: {},
            },
        },
        systemThemeMediaQuery: null,
        handleSystemThemeChange: null,
        concurrencyLimit: {
            enforce: false,
            debug: false,
        },

        aiActions: null,
        integrations,
        integrationTool: {
            opened: false,
            name: null,
            text: '',
            action: '',
        },

        AiIntegrations: {
            enabled: false,
            text: '',
            action: '',
        },

        copyContentToClipboard: {
            enabled: true,
            preventCopyToClipboardEvent: true,
        },

        toast: {
            opened: false,
            text: '',
        },
        onboarding: {
            hasToBeShown: false,
        },

        promoteApp: {
            enabled: false,
            hasToBeShown: false,
            androidAppLink: '',
            appleAppLink: '',
            appName: '',
            appIcon: '',
        },

        // check merge into one object
        highlightModel: {},
        currentHighlight: {},
        selectionsHaveBeenFetched: false,
        selections: [],
        defaultColors: [
            {
                name: 'one',
                code: '#03a9f4',
                lang: 'blue',
            },
            {
                name: 'two',
                code: '#ff4081',
                lang: 'pink',
            },
            {
                name: 'three',
                code: '#ff5722',
                lang: 'orange',
            },
            {
                name: 'four',
                code: '#ffeb3b',
                lang: 'yellow',
            },
        ],

        /**
         * From Scimia
         */
        isFixedLayout: false,
        lastLocation: {
            epub: {
                chapterHref: '',
                cfi: '',
                locator: null, // R2 Locator object
            },
            audio: {
                currentSeek: 0,
                currentTrack: 0,
            },
        },
        locationBeforeFontChange: {
            chapterHref: '',
            cfi: '',
        },
        settingsChange: false,
        doNotTrackFontLoc: false,
        hasLoadLastLocation: false,
        context: [],
        settings: {
            fontSize: 1, // REM value (1 = 100%, 0.75 = 75%, 4 = 400%)
            scroll: 'auto', // auto | scroll-continuous
            syntheticSpread: 'single', // double | single
        },
        readium: null,
        epubViewer: null,
        epub: {
            direction: 'ltr',
            toc: [],
            filePath: null, // Set dynamically from testDataLoader in dev mode
            spine: [],
            totalWords: 0,
            subSpine: {},
            totalPositions: 0, // R2: total number of positions from EpubNavigator
        },
        userSettings: DEFAULT_USER_SETTINGS(),
        isOffline: false,
        epubLayouts: {
            single: {
                scroll: 'auto',
                syntheticSpread: 'single',
            },
            scroll: {
                scroll: 'scroll-doc',
                syntheticSpread: 'auto',
            },
            double: {
                scroll: 'auto',
                syntheticSpread: 'auto',
            },
        },
        addToLists: {
            show: false,
            iframeUrl: '',
        },
        selectionInProgress: false,
    },
    getters: {
        isEmbeddedMode({ embeddedMode }) {
            return embeddedMode;
        },
        canUseNativeShare(state, getters) {
            /**
             * In tartaruga, fenice or any mobile app we'll always use the OS native share dialog.
             * In smartphones we'll try to use the OS native share dialog.
             * Desktop browsers do not support the OS native share dialog, except for Safari in macOS. But
             * the experience is not good enough so we fallback to our custom UI.
             */
            return (state.embeddedInMobileApp || navigator.share) && !getters.isDesktopBrowser;
        },
        isDesktopBrowser(_getters) {
            const userAgent = navigator.userAgent;

            // Detect if it's an iOS device (iPod, iPhone, or iPad)
            const isIOS = Boolean(userAgent.match(/(iPod|iPhone|iPad)/));

            // Detect if it's using WebKit, common in Safari and other browsers on iOS
            const isAppleWebKit = Boolean(userAgent.match(/AppleWebKit/));

            // If it's an iOS device, it's not a desktop browser
            if (isIOS && isAppleWebKit) {
                return false;
            }

            // Detect desktop Safari (excluding Chrome and Android)
            const isSafari = Boolean(/^((?!chrome|android).)*safari/i.test(userAgent));

            // Detect desktop Chrome (excluding Android)
            const isChrome = Boolean(userAgent.match(/Chrome\/[0-9]+/) && !userAgent.match(/Mobile|Android/));

            // Return true if it's either Safari or Chrome on desktop
            return isSafari || isChrome;
        },
        isFeniceDesktopApp(_getters) {
            return Boolean(navigator.userAgent.match(/Electron/));
        },
        isShareEnabled(_state, _getters) {
            // Change this getter to hide the share button
            return true;
        },
        isPreviewMode({ previewMode }) {
            return previewMode;
        },
        isCustomPreview({ customPreview }) {
            return customPreview;
        },
        showBuyOrLoginButton({ isBuyable, userIsGuest, previewMode }) {
            // Show the button if the publication is buyable or if the user is guest and the preview mode is enabled
            // When the publication is free, the button is not shown
            return isBuyable || (userIsGuest && previewMode);
        },
        isEpub({ fileType }) {
            return fileType === 'epub';
        },
        isPdf({ fileType }) {
            return fileType === 'pdf';
        },
        isAudiobook({ fileType }) {
            return fileType === 'audio';
        },
        singlePageMode(state, { isEpub }) {
            if (isEpub) {
                return state.settings.scroll === 'auto' && state.settings.syntheticSpread === 'single';
            }
            return state.reader.layout === 'single';
        },
        doublePageMode(state, { isEpub }) {
            if (isEpub) {
                return state.settings.scroll === 'auto' && state.settings.syntheticSpread === 'auto';
            }
            return state.reader.layout === 'double';
        },
        scrollPageMode(state, { isEpub, isPreviewMode }) {
            // Prevent scroll mode in EPUB previews
            if (isEpub && isPreviewMode) {
                return false;
            }

            if (isEpub) {
                return state.settings.scroll !== 'auto';
            }

            return state.reader.layout === 'scroll';
        },
        publicationNumberOfPages(state, { isEpub }) {
            // For EPUB R2: use total positions from navigator
            if (isEpub && state.epub.totalPositions) {
                return state.epub.totalPositions;
            }
            // For PDF: using files_urls instead of files_info because files_info has all the pages, ignoring the preview mode
            return state.publication.files_urls.length;
        },
        currentPage(state) {
            return state.reader.pagination.current;
        },
        /**
         * Returns the current reading location based on file type.
         * For EPUB files, returns the EPUB location object; for other formats, returns the page number.
         * @returns {Object|number}
         */
        currentLocation(state, { isEpub, currentPage }) {
            return isEpub ? state.lastLocation.epub : currentPage;
        },
        currentPageIsFirstPage(state, { isEpub, currentPage }) {
            if (isEpub) {
                return state.reader.pagination.isFirstPage;
            }
            return currentPage === 1;
        },
        currentPageIsLastPage(state, { isEpub, currentPage, publicationNumberOfPages }) {
            if (isEpub) {
                return state.reader.pagination.isLastPage;
            }
            return currentPage === publicationNumberOfPages;
        },
        /**
         * In doublePageMode `state.ui.pagination.current` is the first
         * if the two pages being displayed
         */
        currentPages(
            { reader: _reader },
            { currentPage, doublePageMode, currentPageIsFirstPage, currentPageIsLastPage, publicationNumberOfPages }
        ) {
            if (!doublePageMode) {
                return [currentPage];
            }
            // In doublePageMode the first page is displayed individually
            if (currentPageIsFirstPage) {
                return [currentPage];
            }
            // In doublePageMode the last page might be displayed individually
            if (publicationNumberOfPages % 2 === 0 && currentPageIsLastPage) {
                return [currentPage];
            }
            return [currentPage, currentPage + 1];
        },
        hasTableOfContents(state) {
            return Array.isArray(state.publication.table_of_contents) && state.publication.table_of_contents.length > 0;
        },
        /**
         * It's `pages` cause the reader could be working in double page mode
         * In doublePageMode `state.ui.pagination.current` is the first
         * if the two pages being displayed
         */
        currentPagesHighlights({ selections }, { currentPages }) {
            // console.log('currentPages', currentPages);
            return selections.filter(highlight => {
                const startsInThisPage = currentPages.includes(highlight.startPage);
                const endsInThisPage = currentPages.includes(highlight.endPage);
                const containsThisPage =
                    currentPages.length === 1
                        ? highlight.startPage < currentPages[0] && currentPages[0] < highlight.endPage
                        : (highlight.startPage < currentPages[0] && currentPages[0] < highlight.endPage) ||
                          (highlight.startPage < currentPages[1] && currentPages[1] < highlight.endPage);
                return startsInThisPage || endsInThisPage || containsThisPage;
            });
        },
        hasFeatureActive(state) {
            return (
                state.ui.notebook.opened ||
                state.ui.thumbnails.opened ||
                state.ui.tableOfContents.opened ||
                state.ui.search.opened
            );
        },
        onlyDesktopVisible(state, getters) {
            // Assumes that if the device is not mobile (based on the mobile visibility criteria), it is considered desktop
            return !getters.onlyMobileVisible;
        },
        onlyMobileVisible(state) {
            // This evaluates the real device width
            return Math.min(state.device.width, state.device.height) <= 570;
        },
        showHighlightsAndNotes(state, _getters) {
            return !state.ui.features.notes.opened && !state.ui.features.highlights.opened;
        },
        canUseNotes(state, getters) {
            return !getters.isAudiobook;
        },
        canUseReaderPlugins(state, getters) {
            return getters.isPdf && state.ui.plugin.enabled && state.ui.plugin.src;
        },
        /**
         * Calculates the content offset when a persistent sidebar panel is open on desktop.
         * @param {Object} state - Vuex state
         * @param {Object} getters - Vuex getters
         * @returns {{ left: number, right: number }} Offset in pixels for each side
         */
        sidebarContentOffset(state, getters) {
            if (getters.onlyMobileVisible) {
                return { left: SIDEBAR_NO_OFFSET, right: SIDEBAR_NO_OFFSET };
            }

            const left =
                state.ui.tableOfContents.opened || state.ui.notebook.opened || state.ui.audiobook.playlist.opened
                    ? SIDEBAR_PANEL_OFFSET
                    : SIDEBAR_NO_OFFSET;

            const right = state.ui.plugin.opened
                ? SIDEBAR_PLUGIN_OFFSET
                : state.ui.search.opened || state.ui.settings.opened
                  ? SIDEBAR_PANEL_OFFSET
                  : SIDEBAR_NO_OFFSET;

            return { left, right };
        },
        isAndroid() {
            return detectAndroid();
        },
        isApple() {
            return detectApple();
        },
        isPromoteAppEnabled(state, getters) {
            if (state.embeddedInMobileApp || getters.isFeniceDesktopApp) {
                return false;
            }
            return state.promoteApp.enabled;
        },
        showRangePaginator(state, getters) {
            if (getters.isPdf || (getters.isEpub && state.epub.spine?.length > 0)) {
                return true;
            }

            return false;
        },
        isZoomActive(state) {
            return state.ui.settings.zoom.select.scale !== 1;
        },
        highlightColorsCode(state) {
            return state.defaultColors.map(color => {
                return color.code;
            });
        },
        isOpenSomeFeatureOrSettings(state) {
            const features = [
                state.ui.search,
                state.ui.thumbnails,
                state.ui.settings,
                state.ui.listening,
                state.ui.notebook,
                state.ui.plugin,
                state.ui.rightSubmenu,
                state.ui.tableOfContents,
                state.ui.audiobook.playlist,
            ];

            return features.some(feature => feature?.opened === true);
        },
    },
    actions: {
        copyToClipBoard({ state }, selectionText) {
            /**
             *  If the feature itself is disabled, which could be
             *  the intention of the content owner, then we return
             *  early and prevent the action.
             *  This is a fail-safe in case the action is
             *  triggered even if it shouldn't.
             */
            if (!state.copyContentToClipboard.enabled) {
                return;
            }
            state.copyContentToClipboard.preventCopyToClipboardEvent = false;
            uiAPI.copyToClipboard({ text: selectionText });
            state.copyContentToClipboard.preventCopyToClipboardEvent = true;
        },
        /**
         * Our default action is to prevent content to be copied to the clipboard.
         * The copying to the clipboard event may be generated by copy or cut actions.
         * Those may be triggered by a browser or OS native UI,
         * by keyboard shortcuts or even from JS itself.
         */
        preventUnauthorizedCopy({ state, _getters }, e) {
            /**
             *  If copy and cut events are temporality allowed,
             *  return early and allow the content to be copied.
             *  This flag is controlled exclusively by the
             *  copyToClipBoard method, and it's used to
             *  temporality allow the action.
             */
            if (!state.copyContentToClipboard.preventCopyToClipboardEvent) {
                return;
            }
            e.preventDefault();
            e.clipboardData.setData('text/plain', '');
        },
        trackOnboarding({ _state, _getters }, _payload) {
            /**
             * We used to use Mixpanel.
             * I'm leaving this here cause we'll implement something similar at some point.
             */
        },
        trackEvent({ _getters }, _payload) {
            /**
             * We used to use Mixpanel.
             * I'm leaving this here cause we'll implement something similar at some point.
             */
        },
        async goToNextPage({ dispatch, state, getters, _commit }, payload) {
            if (state.isFixedLayout && state.isLoading) return;
            if (state.disableNext) {
                /*
                 * Show payments link when an user go to last page in the preview and
                 * the issue need is purchable.
                 */
                navigationAPI.reachLastPage();
                return;
            }

            const direction = 'next';
            await dispatchAnimation(direction, payload?.skipAnimation ?? getters.scrollPageMode);
            if (getters.isEpub) {
                dispatch('goToLocation', { direction });
                return;
            }

            const offset = getters.doublePageMode && getters.currentPage !== 1 ? 2 : 1;
            const destPage =
                getters.currentPage < getters.publicationNumberOfPages
                    ? getters.currentPage + offset
                    : getters.publicationNumberOfPages;

            // In PDFs if destPage is the last page, it means that the user is at the end of the publication
            // The host is responsible for showing the payments link or any other action
            if (destPage === getters.publicationNumberOfPages) {
                navigationAPI.reachLastPage();
            }
            dispatch('goToPage', {
                page: destPage,
                action: 'next',
            });
        },
        async goToPrevPage({ dispatch, state, getters, commit }, payload) {
            if (state.isFixedLayout && state.isLoading) return;
            commit('setLastPageReached', false);
            state.disableNext = false;

            const direction = 'prev';
            await dispatchAnimation(direction, payload?.skipAnimation ?? getters.scrollPageMode);
            if (getters.isEpub) {
                dispatch('goToLocation', { direction });
                return;
            }
            const offset = getters.doublePageMode && getters.currentPage !== 1 && getters.currentPage !== 2 ? 2 : 1;
            const destPage = getters.currentPage > 1 ? getters.currentPage - offset : 1;
            dispatch('goToPage', {
                page: destPage,
                action: 'prev',
            });
        },
        goToPage({ _state, getters, commit, dispatch }, payload) {
            commit('closeSelectionTools');

            let destPage;
            // console.log('payload.page', payload.page);
            if (!getters.doublePageMode) {
                // console.log('single page mode');
                destPage = payload.page;
            } else if (payload.page === 1) {
                // console.log('payload.page === 1');
                // In doublePageMode the first page is displayed individually
                destPage = payload.page;
            } else if (payload.page === getters.publicationNumberOfPages) {
                // console.log('payload.page === getters.publicationNumberOfPages');
                // In doublePageMode the first page is displayed individually
                destPage = payload.page;
            } else if (payload.page % 2 !== 0) {
                // console.log('payload.page % 2', payload.page % 2);
                // In doublePageMode the last page might be displayed individually
                destPage = payload.page - 1;
            } else if (payload.page % 2 === 0 && payload.page - 1 === getters.publicationNumberOfPages) {
                // console.log('payload.page % 2', payload.page % 2, getters.currentPages[0]);

                if (getters.currentPages[0] === payload.page - 2) {
                    // console.log('payload.page - 2', payload.page - 2)
                    destPage = payload.page - 2;
                } else {
                    // console.log('payload.page - 1', payload.page - 1)
                    destPage = payload.page - 1;
                }
            } else {
                // console.log('eeelse', payload.page);
                destPage = payload.page;
            }

            // console.log('destPage', destPage);

            // @here
            // Do nothing if destPage === currentPage

            commit('setPage', { page: destPage });
            dispatch('notifyLocationChange');
        },
        goToLocation({ state, getters, commit, _dispatch }, payload) {
            commit('closeSelectionTools');

            // TODO: state.epubViewer should eventually replace state.readium here.
            // Currently both coexist: state.readium stores the EpubNavigator instance
            // and state.epubViewer stores the EpubViewer wrapper. Consolidate into
            // a single reference to avoid dual-state confusion.
            if (getters.isEpub && state.readium) {
                // Readium 2 navigation
                if (payload.direction === 'next') {
                    state.readium.goRight(true, success => {
                        if (!success || state.epubViewer?.isLastPageVisible()) {
                            state.disableNext = true;
                            navigationAPI.reachLastPage();
                        }
                    });
                } else if (payload.direction === 'prev') {
                    state.readium.goLeft(true, () => {});
                } else if (payload.epubR2Location?.locations) {
                    // R2 navigation from epubR2Location (notes/highlights)
                    const locator = createR2Locator(payload.epubR2Location);
                    state.readium.go(locator, true, () => {});
                } else if (payload.epubR2PositionIndex !== undefined) {
                    // R2 navigation by position index (from range paginator)
                    // Accesses EpubNavigator.positions[] directly since state.readium
                    // stores the navigator instance, not the EpubViewer wrapper
                    const locator = state.readium.positions[payload.epubR2PositionIndex];
                    if (locator) {
                        state.readium.go(locator, true, () => {});
                    }
                } else if (payload.epubLocation) {
                    // Legacy R1 format: delegate to EpubViewer for idref resolution and navigation
                    state.epubViewer?.goToR1Location(payload.epubLocation, payload.totalText);
                } else if (payload.chapterHref) {
                    // From Table of Contents
                    const link = new Link({ href: payload.chapterHref });
                    state.readium.goLink(link, true, () => {});
                }
            } else if (state.readium?.reader) {
                // Legacy R1 fallback (should not happen after full migration)
                if (payload.direction === 'next') {
                    state.readium.reader.openPageRight();
                } else if (payload.direction === 'prev') {
                    state.readium.reader.openPageLeft();
                }
            }
        },

        setLastLocation({ state, getters, _commit, dispatch }, payload) {
            if (getters.isEpub) {
                // Support both R2 locator format and legacy R1 format
                if (payload.locator) {
                    // R2 format: store the full locator
                    state.lastLocation.epub = {
                        locator: payload.locator,
                        // Keep legacy fields for backward compatibility with host apps
                        chapterHref: payload.locator.href || '',
                        cfi: '',
                    };
                } else {
                    // Legacy R1 format
                    state.lastLocation.epub.chapterHref = payload.chapterHref;
                    state.lastLocation.epub.cfi = payload.cfi;
                }
            }
            if (getters.isAudiobook) {
                state.lastLocation.audio.currentSeek = payload.currentSeek;
                state.lastLocation.audio.currentTrack = payload.currentTrack;
            }
            if (payload.dontNotify) return;
            dispatch('notifyLocationChange');
        },
        notifyLocationChange({ state, getters }) {
            let location = {};

            if (getters.isPdf) {
                location = { page: getters.currentPage };
            }
            if (getters.isEpub) {
                // Send R2 locator data to host if available, otherwise legacy format
                const epubLocation = state.lastLocation.epub;
                if (epubLocation.locator) {
                    location = {
                        locator: epubLocation.locator,
                        // Include legacy fields for backward compatibility
                        chapterHref: epubLocation.chapterHref,
                        cfi: epubLocation.cfi,
                    };
                } else {
                    location = epubLocation;
                }
            }

            if (getters.isAudiobook) {
                location = state.lastLocation.audio;
            }

            localStorage?.setItem('notifyLocationChange', JSON.stringify(location));

            /**
             * It is up to the host to throttle these events.
             */
            navigationAPI.locationChanged({
                location,
                fileType: state.fileType,
            });
        },
        setDisableNext({ state }, payload) {
            state.disableNext = payload.disableNext;
        },

        async fetchSelections({ state, commit }) {
            if (state.anonymousSession) {
                commit('fetchedSelections');
                return;
            }

            if (state.selectionsHaveBeenFetched) return;
            try {
                const highlights = await notesAPI.list();
                commit('setHighlights', { highlights });
                commit('fetchedSelections');
            } catch (e) {
                console.error('fetchSelections failed', e);
                commit('setHighlights', {
                    highlights: [],
                });
                commit('fetchedSelections');
            }
        },
        // #tts
        listeningPage({ state, commit }) {
            const isListeningPage = state.ui.listening.texToListen === state.ui.listening.pageToListen;
            commit('setUuidToListen');
            if (state.ui.listening.opened && isListeningPage) {
                commit('closeListeningPlayer');
            } else if (isListeningPage) {
                commit('closeAllSettingsOrFeatures', { featureKey: 'listening' });
                commit('openListeningPlayer');
            } else {
                commit('closeAllSettingsOrFeatures', { featureKey: 'listening' });
                commit('openListeningPlayer');
                commit('loadPageTextToTTS');
            }
        },
        listeningHighlights({ commit }, payload) {
            commit('setTextToListen', { text: payload.text });
            commit('setUuidToListen', { uuidToListen: payload.uuidToListen });
            commit('openListeningPlayer');
        },

        setTheme({ commit, dispatch, state }, payload) {
            if (BASE_THEMES.includes(payload.theme)) {
                commit('setBaseTheme', { theme: payload.theme });
            }

            let colorScheme = payload.theme;
            commit('setUiTheme', { theme: payload.theme });
            if (state.ui.theme === 'system') {
                colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }

            commit('setThemeSelected', { theme: colorScheme });
            dispatch('applyTheme', { theme: payload.theme });
        },
        applyTheme({ _state }, payload) {
            const body = document.querySelector('body');
            const darkThemes = ['dark', 'high-contrast-dark'];
            if (darkThemes.includes(payload.theme)) {
                body.classList.add('dark');
            } else {
                body.classList.remove('dark');
            }

            localStorage.setItem('setTheme', payload.theme);
        },

        updateUserSettings({ state }, payload) {
            const hasEpubChanges = payload.epubLayout || payload.epubZoom || payload.fontFamily !== undefined;

            if (hasEpubChanges) {
                state.userSettings.epub = {
                    ...state.userSettings.epub,
                    ...(payload.epubLayout && { layout: payload.epubLayout }),
                    ...(payload.epubZoom && { zoom: payload.epubZoom }),
                    ...(payload.fontFamily !== undefined && { fontFamily: payload.fontFamily }),
                };
            }

            if (payload.pdfLayout) {
                state.userSettings.pdf = { ...state.userSettings.pdf, layout: payload.pdfLayout };
            }

            if (payload.theme) {
                state.userSettings.theme = payload.theme;
            }

            if (state.anonymousSession) return;
            state.userSettings.epub.layout =
                payload.epubLayout !== undefined ? payload.epubLayout : state.userSettings.epub.layout;
            state.userSettings.epub.zoom =
                payload.epubZoom !== undefined ? payload.epubZoom : state.userSettings.epub.zoom;
            state.userSettings.epub.fontFamily =
                payload.fontFamily !== undefined ? payload.fontFamily : state.userSettings.epub.fontFamily;
            state.userSettings.epub.lineHeight =
                payload.lineHeight !== undefined ? payload.lineHeight : state.userSettings.epub.lineHeight;
            state.userSettings.epub.margins =
                payload.margins !== undefined ? payload.margins : state.userSettings.epub.margins;
            state.userSettings.pdf.layout =
                payload.pdfLayout !== undefined ? payload.pdfLayout : state.userSettings.pdf.layout;
            state.userSettings.theme = payload.theme !== undefined ? payload.theme : state.userSettings.theme;

            settingsAPI.update({ settings: state.userSettings });
        },
        resetUserSettings({ state, commit, dispatch }) {
            dispatch('setTheme', { theme: 'light' });
            commit('fontSizeUpdate', { fontSize: FONT_SIZE_DEFAULT });
            state.userSettings = DEFAULT_USER_SETTINGS();
            if (!state.anonymousSession) {
                settingsAPI.update({ settings: state.userSettings });
            }
            commit('setReaderLayout', 'single');
        },
        setUserSettings({ state, commit, getters }, settings) {
            state.userSettings = settings;

            if (BASE_THEMES.includes(state.userSettings.theme)) {
                commit('setBaseTheme', { theme: state.userSettings.theme });
            }
            commit('setUiTheme', { theme: state.userSettings.theme });
            if (getters.isEpub) {
                // Prevent scroll mode in EPUB previews
                const epubLayout = ((state, getters) => {
                    let preliminaryEpubLayout = state.userSettings.epub.layout || state.reader.layout || 'single';
                    if (preliminaryEpubLayout === 'scroll' && getters.isPreviewMode) {
                        preliminaryEpubLayout = 'single';
                    }
                    return state.epubLayouts[preliminaryEpubLayout];
                })(state, getters);

                const zoomFromApi = state.userSettings.epub.zoom;
                const storedFontSize = state.settings?.fontSize;
                let zoomInRem = storedFontSize;
                if (zoomFromApi) {
                    if (zoomFromApi >= API_FONT_SIZE_MIN && zoomFromApi <= API_FONT_SIZE_MAX) {
                        zoomInRem = zoomFromApi / API_FONT_SIZE_BASE;
                    } else if (zoomFromApi >= FONT_SIZE_MIN && zoomFromApi <= FONT_SIZE_MAX) {
                        zoomInRem = zoomFromApi;
                    }
                }

                commit('fontSizeUpdate', { fontSize: zoomInRem });
                commit('setEpubLayoutMode', epubLayout);
            }
            if (state.userSettings.pdf.layout && getters.isPdf) {
                commit('setReaderLayout', state.userSettings.pdf.layout);
            }
            if (getters.isPdf && getters.doublePageMode && getters.onlyMobileVisible) {
                commit('setReaderLayout', 'single');
            }
        },

        openNotesModal({ dispatch, commit, getters }) {
            if (getters.onlyMobileVisible) {
                dispatch('toggleUi', { uiHidden: true });
            }
            commit('openNotesModal');
        },

        showSessionExpiredModal({ _state, commit }) {
            commit('setSessionExpired');
        },

        buildUrlAndGoToSocialNetworksSharing(
            { state, _dispatch },
            payload = {
                url: null,
                network: null,
                title: null,
                authors: null,
                highlight: null,
                readerUrl: null,
            }
        ) {
            let url = payload.url;

            if (payload.network === 'x' && state.issue.twitterUser === '') {
                url = url.replace('&via=@xuser', '');
            }

            if (!payload.authors) {
                url = url.replace('@by%20@authors%20', '');
            }

            uiAPI.shareContent({
                url: url
                    .replace(/@iamread/g, encodeURIComponent(state.localizations[state.lang].imReading))
                    .replace(/@highlight/g, encodeURIComponent(payload.highlight))
                    .replace(/@title/g, encodeURIComponent(payload.title))
                    .replace(/@by/g, encodeURIComponent(state.localizations[state.lang].by.toLowerCase()))
                    .replace(/@authors/g, encodeURIComponent(payload.authors))
                    .replace(/@in/g, encodeURIComponent(state.localizations[state.lang].in))
                    .replace(/@url/g, encodeURIComponent(payload.readerUrl))
                    .replace(
                        /@description/g,
                        encodeURIComponent(state.issue.descriptionRawText ? state.issue.descriptionRawText : '')
                    )
                    .replace(/@twitteruser/g, state.issue.twitterUser),
                target: payload.network === 'email' || state.embeddedInMobileApp ? '_top' : '_blank',
            });
        },
        openOnboarding({ _state, commit }) {
            try {
                if (!localStorage.getItem('onboardingHasBeenShown:v1.1')) {
                    commit('openOnboarding');
                    localStorage.setItem('onboardingHasBeenShown:v1.1', 'true');
                }
            } catch (_error) {
                commit('openOnboarding');
                settingsAPI.cookiesAreDisabled();
            }
        },
        initToggleUi({ getters, _commit, dispatch }, payload) {
            if (getters.isAudiobook) {
                payload.uiHidden = false;
            }
            dispatch('toggleUi', payload);
        },
        closeSmartZoomOrVolpe({ state, commit }) {
            if (!state.embeddedInMobileApp) return;

            if (state.smartZoom.visible) {
                commit('toggleSmartZoom');
            } else {
                uiAPI.close();
            }
        },
        openTranslateIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'translate',
                text: payload.text,
            });
        },
        openDictionaryIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'dictionary',
                text: payload.text,
            });
        },
        openAiIntegration({ dispatch, _commit }, payload) {
            dispatch('openIntegrationTool', {
                name: 'ai-integrations',
                text: payload.text,
                action: payload.action,
            });
        },
        openIntegrationTool({ commit }, payload) {
            commit('toggleIntegrationTool', {
                opened: true,
                name: payload.name,
                text: payload.text,
                action: payload.action,
            });
        },
        closeIntegrationTool({ commit }) {
            commit('toggleIntegrationTool', {
                opened: false,
                integration: null,
                text: '',
            });
        },
        setCurrentEpubProgress({ state, commit }, payload) {
            if (payload.spineItem || payload.openPage) {
                // We need to add +1 in separated way to avoid NaN when the first value is index == 0
                commit('setPage', {
                    page: payload.spineItem?.index + 1 || payload.openPage.spineItemIndex + 1,
                });
                commit('setSubSpine', {
                    subSpine: {
                        pages: payload.openPage?.spineItemPageCount || payload.spineItem?.paginationInfo?.spreadCount,
                        position:
                            payload.openPage?.spineItemPageIndex + 1 ||
                            payload.spineItem?.paginationInfo?.currentSpreadIndex + 1,
                    },
                });
                return;
            }

            const cfi = state.readium.reader.getFirstVisibleCfi();
            const spineLocation = state.epub.spine?.findIndex(spine => spine.href.includes(cfi.idref));
            commit('setPage', { page: spineLocation + 1 || 1 });
        },
        async fetchAiActions({ state, commit }) {
            if (!state.AiIntegrations.enabled) return;

            const actions = await aiIntegrationsAPI.listActions().catch(_error => {
                return [];
            });

            commit('aiActions', actions || []);
        },

        /// new ones
        toggleUi({ _state, commit }, payload) {
            // Exclude listening because is not a good experience stop de player when is hide the UI
            commit('closeAllSettingsOrFeatures', { featureKey: 'listening' });
            if (!payload || payload.uiHidden === undefined) {
                commit('toggleHeaderAndFooter');
            } else {
                commit('setStateUiHidden', payload);
            }
        },

        toggleSettings({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'settings' });
            commit('toggleFeature', { featureKey: 'settings' });
        },

        toggleRightSubmenu({ commit, _state }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'rightSubmenu' });
            commit('toggleFeature', { featureKey: 'rightSubmenu' });
        },

        toggleTableOfContents({ commit, _state }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'tableOfContents' });
            commit('toggleFeature', { featureKey: 'tableOfContents' });
        },

        toggleSearch({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'search' });
            commit('toggleFeature', { featureKey: 'search' });
        },

        openSearch({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'search' });
            commit('openFeature', { featureKey: 'search' });
        },

        toggleNotebook({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'notebook' });
            commit('toggleFeature', { featureKey: 'notebook' });
        },

        showHighlightsAndNotesTab({ commit, state }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'highlights' });
            commit('closeFeature', { featureKey: 'features', subFeature: 'notes' });
            if (state.ui.selectionTool.opened) {
                commit('hideSelectionTool');
            }
        },

        toggleHighlights({ commit }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'notes' });
            commit('openFeature', { featureKey: 'features', subFeature: 'highlights' });
        },

        toggleNotes({ commit }) {
            commit('closeFeature', { featureKey: 'features', subFeature: 'highlights' });
            commit('openFeature', { featureKey: 'features', subFeature: 'notes' });
        },

        togglePlugin({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'plugin' });
            commit('toggleFeature', { featureKey: 'plugin' });
        },

        toggleThumbnails({ commit }) {
            commit('closeAllSettingsOrFeatures', { featureKey: 'thumbnails' });
            commit('toggleFeature', { featureKey: 'thumbnails' });
        },

        initializeThemeListener({ state, commit }) {
            state.systemThemeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

            if (state.ui.theme === 'system') {
                commit('setThemeSelected', {
                    theme: state.systemThemeMediaQuery.matches ? 'dark' : 'light',
                });
            }

            state.handleSystemThemeChange = e => {
                if (state.ui.theme !== 'system') return;
                commit('setThemeSelected', { theme: e.matches ? 'dark' : 'light' });
            };

            const mediaQuery = state.systemThemeMediaQuery;

            if (typeof mediaQuery.addEventListener === 'function') {
                mediaQuery.addEventListener('change', state.handleSystemThemeChange);
            } else if (typeof mediaQuery.addListener === 'function') {
                /**
                 * Added support for older versions of iOS (<14)
                 * that do not support the 'addEventListener' method for MediaQueryList.
                 */
                mediaQuery.addListener(state.handleSystemThemeChange);
            } else {
                console.warn('MediaQueryList is not supported for this browser.');
            }
        },
    },
    mutations: {
        plaPublicationManifestVersion(state, payload) {
            state.plaPublicationManifestVersion = payload.plaPublicationManifestVersion;
        },
        setTenantId(state, payload) {
            state.tenantId = payload.tenantId;
        },
        contentEncryptionIvSeed(state, payload) {
            state.contentEncryption.ivSeed = {
                tenant_id: payload.tenant_id,
                issue_id: payload.issue_id,
            };
        },
        coniglioTrackToken(state, payload) {
            state.coniglio.track_token = payload.coniglio_track_token;
        },
        setCopyContentToClipboard(state, payload) {
            state.copyContentToClipboard.enabled = payload.enabled;
        },
        loadingProcessIsFinished(state) {
            if (state.isLoadingProcessFinished) throw 'LoadingProcess should only be done once';
            state.isLoadingProcessFinished = true;
        },
        embeddedMode(state, payload) {
            state.embeddedMode = payload.embeddedMode;
        },
        previewMode(state, payload) {
            state.previewMode = payload.previewMode;
        },
        customPreview(state, payload) {
            state.customPreview = payload.customPreview;
        },
        setFileType(state, payload) {
            state.fileType = payload.fileType;
        },
        setEpubFilePath(state, payload) {
            state.epub.filePath = payload.filePath;
        },
        inIframe(state, payload) {
            state.inIframe = payload.inIframe;
        },
        setIssueId(state, payload) {
            state.issue.id = payload.issue_id;
        },
        polpoSessionHasBeenInitiated(state) {
            state.polpoSessionInitiated = true;
        },
        setReaderToken(state, payload) {
            state.readerToken = payload.token;
        },
        anonymousSession(state, payload) {
            state.anonymousSession = payload.anonymous_session;
        },
        setDeviceDetails(state, payload) {
            state.device.width = payload.width;
            state.device.height = payload.height;
            state.device.orientation = payload.orientation;
        },
        parentUrl(state, payload) {
            state.parentUrl = payload.url;
        },
        setLoadingState(state, payload) {
            state.isLoading = payload.isLoading;
            state.loadingSpinnerPosition = payload.position;
        },
        setTrackPlayerState(state, payload) {
            state.audiobook.trackPlayer.isLoading = payload.isLoading;
            state.audiobook.trackPlayer.isPlaying = payload.isPlaying;
        },
        setCurrentTrackDurationInSecondsState(state, payload) {
            state.audiobook.currentTrackDurationInSeconds = payload.durationInSeconds;
        },
        lang(state, payload) {
            state.lang = payload.lang;
            // Update document lang attribute for accessibility
            if (payload.lang) {
                document.documentElement.lang = payload.lang;
            }
        },
        filesUrls(state, payload) {
            state.publication.files_urls = payload.files_urls;
        },
        filesInfo(state, payload) {
            state.publication.files_info = payload.files_info;
        },
        url(state, payload) {
            state.publication.url = payload.url;
        },
        tableOfContents(state, payload) {
            state.publication.table_of_contents = payload.table_of_contents;
        },
        title(state, payload) {
            state.title = payload.title;
        },
        /**
         * Updates the browser's document.title for accessibility.
         * @param {Object} payload - The payload object
         * @param {string} [payload.context] - Context-specific part (chapter title, page number, track name)
         */
        updateDocumentTitle(state, payload = {}) {
            const bookTitle = state.title || 'Reader';
            const context = payload.context;

            document.title = context ? `${bookTitle} - ${context}` : bookTitle;
        },
        issueTitle(state, payload) {
            state.issue.title = payload.title;
        },
        tags(state, payload) {
            state.issue.tags = payload.tags;
        },
        issueUrl(state, payload) {
            state.issue.issue_url = payload.issue_url;
        },
        shareUrl(state, payload) {
            state.issue.share_url = payload.share_url;
        },
        setIssueLang(state, payload) {
            state.issue.lang = payload.lang;
        },
        publicationDate(state, payload) {
            state.issue.publication_date = payload.publication_date;
        },
        publicationPlace(state, payload) {
            state.issue.publicationPlace = payload.publicationPlace;
        },
        publicationCover(state, payload) {
            state.publication.cover = payload.cover;
        },
        publicationAbout(state, payload) {
            state.issue.aboutThisPublication = payload.publication_about;
        },
        descriptionRawText(state, payload) {
            state.issue.descriptionRawText = payload.description_raw_text;
        },
        externalId(state, payload) {
            state.issue.externalId = payload.external_id;
        },

        // UI behaviour
        toggleHeaderAndFooter(state) {
            state.uiHidden = !state.uiHidden;
        },
        setStateUiHidden(state, payload) {
            state.uiHidden = payload.uiHidden;
        },

        openSidebar(state) {
            state.sidebar.opened = true;
            if (state.ui.selectionTool.opened) {
                state.ui.selectionTool.opened = false;
            }
        },
        closeSidebar(state) {
            state.sidebar.opened = false;
        },
        setTotalPages(state, payload) {
            state.epub.totalPositions = payload.total;
            state.reader.pagination.total = payload.total;
            state.reader.pagination.ready = true;
        },
        pauseKeyboardNavigation(state) {
            state.reader.keyboardNavigationEnabled = false;
        },
        resumeKeyboardNavigation(state) {
            state.reader.keyboardNavigationEnabled = true;
        },
        setPage(state, payload) {
            if (state.reader.pagination.current === payload.page) {
                return;
            }
            state.ui.pagination.current = payload.page;
            state.reader.pagination.current = payload.page;
        },
        closeSelectionTools(state) {
            if (state.ui.selectionTool.opened) state.ui.selectionTool.opened = false;
        },
        //ui
        uiOpenSidebar(state) {
            state.ui.sidebar.opened = !state.ui.sidebar.opened;
            if (state.ui.selectionTool.opened && state.ui.sidebar.opened) {
                state.ui.selectionTool.opened = false;
            }
        },

        /// Open and close features logic
        closeAllSettingsOrFeatures(state, { featureKey = null } = {}) {
            Object.keys(state.ui)
                .filter(key => (featureKey ? key !== featureKey : true))
                .forEach(key => {
                    if (!state.ui[key].opened) return;
                    state.ui[key].opened = false;
                });
            Object.keys(state.ui.features)
                .filter(key => (featureKey ? key !== featureKey : true))
                .forEach(key => {
                    if (!state.ui.features[key].opened) return;
                    state.ui.features[key].opened = false;
                });

            Object.keys(state.ui.audiobook).forEach(key => {
                if (!state.ui.audiobook[key].opened) return;
                state.ui.audiobook[key].opened = false;
            });
        },
        toggleFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui.features[featureKey][subFeature].opened = !state.ui.features[featureKey][subFeature].opened;
            } else {
                state.ui[featureKey].opened = !state.ui[featureKey].opened;
            }
        },
        openFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui[featureKey][subFeature].opened = true;
            } else {
                state.ui[featureKey].opened = true;
            }
        },
        closeFeature(state, { featureKey, subFeature = null }) {
            if (subFeature) {
                state.ui[featureKey][subFeature].opened = false;
            } else {
                state.ui[featureKey].opened = false;
            }
        },

        rangePageSelect(state, payload) {
            state.ui.pagination.current = payload.page;
        },

        requestQueryWordsHighlight(state, payload) {
            state.search.requestQueryWordsHighlight = true;
            state.search.queryWords = payload.queryWords;
            state.search.page = payload.page;
        },
        requestClearWordsHighlight(state, payload) {
            state.search.clear = payload;
        },
        fulfillQueryWordsHighlight(state) {
            state.search.requestQueryWordsHighlight = false;
            state.search.page = null;
            state.search.queryWords = [];
        },

        //smart zoom
        setIssueArticles(state, payload) {
            state.articles = payload.articles;
            if (state.articles.length > 0) {
                state.smartZoom.enabled = true;
            }
        },

        increaseSmartZoomFontSizeMultiplier(state) {
            if (state.smartZoom.fontSizeMultiplier < 1.85) {
                state.smartZoom.fontSizeMultiplier = state.smartZoom.fontSizeMultiplier + 0.1;
            }
        },

        decreaseSmartZoomFontSizeMultiplier(state) {
            if (state.smartZoom.fontSizeMultiplier > 0.85) {
                state.smartZoom.fontSizeMultiplier = state.smartZoom.fontSizeMultiplier - 0.1;
            }
        },

        setCurrentPageArticles(state, payload) {
            state.currentPageArticles = payload.currentPageArticles;
        },
        setSharedArticle(state, payload) {
            state.sharedArticle.id = payload.articleId;
            state.sharedArticle.showed = payload.showed;
        },
        prevArticle(state) {
            if (state.smartZoom.currentArticleIndex > 0) {
                state.smartZoom.currentArticleIndex = state.smartZoom.currentArticleIndex - 1;
            }
        },
        nextArticle(state) {
            if (state.smartZoom.currentArticleIndex + 1 < state.currentPageArticles.length) {
                state.smartZoom.currentArticleIndex = state.smartZoom.currentArticleIndex + 1;
            }
        },
        setCurrentArticleIndex(state, payload) {
            state.smartZoom.currentArticleIndex = payload.currentArticleIndex;
        },
        toggleSmartZoom(state) {
            state.smartZoom.visible = !state.smartZoom.visible;
        },
        //end smart zoom
        zoomByType(state, payload) {
            state.ui.settings.zoom.select.isSelected = payload.type;
        },
        setZoomScale(state, payload) {
            state.ui.settings.zoom.select.scale = payload.scale;
        },

        setEnforceConcurrencyLimit(state, payload) {
            state.concurrencyLimit.enforce = payload;
        },
        setDebugEnforceConcurrencyLimit(state, payload) {
            state.concurrencyLimit.debug = payload;
        },

        // #tts
        setTextToListen(state, payload) {
            if (payload.listenPage) {
                state.ui.listening.pageToListen = payload.text;
                if (state.ui.listening.opened) return;
            }
            state.ui.listening.texToListen = payload.text;
        },
        // #selections tool
        fetchedSelections(state, _payload) {
            state.selectionsHaveBeenFetched = true;
        },
        toggleSelectionTool(state) {
            state.ui.selectionTool.opened = !state.ui.selectionTool.opened;
        },
        showSelectionTool(state, payload) {
            state.ui.selectionTool.opened = true;
            state.ui.selectionTool.page = payload;
        },
        hideSelectionTool(state) {
            state.ui.selectionTool.opened = false;
        },
        setCurrentHighlight(state, payload) {
            state.currentHighlight = payload;
        },
        setHighlightModel(state, payload) {
            state.highlightModel = payload;
        },
        async setHighlights(state, payload) {
            state.selections = await Promise.all(
                payload.highlights
                    .map(highlight => {
                        return { ...state.highlightModel, ...highlight, confirmed: true };
                    })
                    .map(async highlight => {
                        return await highlight.reHydrate();
                    })
            );
        },
        removeSelection(state, payload) {
            state.selections.splice(findIndex(state.selections, ['uuid', payload.uuid]), 1);
        },
        setSelectionToolPosition(state, payload) {
            state.ui.selectionTool.position = payload;
        },
        setSelectionPanelSide(state, payload) {
            state.ui.selectionTool.panelSide = payload.side;
            state.ui.selectionTool.selectionTop = payload.selectionTop;
            state.ui.selectionTool.contentLeft = payload.contentLeft || 0;
            state.ui.selectionTool.contentRight = payload.contentRight || 0;
        },
        pushSelection(state, payload) {
            state.selections.push(payload);
        },
        updateSelection(state, payload) {
            const index = findIndex(state.selections, ['uuid', payload.uuid]);
            state.selections.splice(index, 1, { ...state.selections[index], ...payload });
        },

        StandAloneAllFeatures(state) {
            state.ui.features.bookmarks.enabled = true;
            state.ui.search.enabled = true;
            state.ui.settings.enabled = true;
            state.ui.listening.enabled = true;
        },
        toggleIntegrationTool(state, payload) {
            state.integrationTool.opened = payload.opened;
            state.integrationTool.name = payload.name;
            state.integrationTool.text = payload.text;
            state.integrationTool.action = payload.action;
        },
        setAiIntegrationsEnabledOrDisabled(state, payload) {
            state.AiIntegrations.enabled = payload.enabled;
        },
        setFakeIssue(state, payload) {
            state.issue = payload.issue;
        },
        openToast(state, payload) {
            state.toast.opened = true;
            state.toast.text = payload.text;
        },
        closeToast(state) {
            state.toast.opened = false;
            state.toast.text = '';
        },
        openOnboarding(state) {
            state.onboarding.hasToBeShown = true;
        },
        closeOnboarding(state) {
            state.onboarding.hasToBeShown = false;
        },
        openPromoteApp(state) {
            state.promoteApp.hasToBeShown = true;
        },
        closePromoteApp(state) {
            state.promoteApp.hasToBeShown = false;
        },
        // #tts
        loadPageTextToTTS(state) {
            state.ui.listening.texToListen = state.ui.listening.pageToListen;
        },
        openListeningPlayer(state) {
            state.ui.settings.opened = false;
            state.ui.listening.opened = true;
        },
        closeListeningPlayer(state) {
            state.ui.listening.opened = false;
        },
        setListeningEnabled(state, payload) {
            state.ui.listening.enabled = payload.tts_enabled;
        },
        setExtraVoices(state, payload) {
            state.ui.extraVoices.chinese = payload.chinese;
        },
        setListeningDriver(state, payload) {
            state.ui.listening.driver = payload.tts_driver;
        },
        setSearchEnabled(state, payload) {
            state.ui.search.enabled = payload.search_enabled;
        },
        setReaderLayout(state, layout) {
            /**
             * This layout information is using to update the component type for rendering single or double view in PDF
             * and affect when is change in EPUBs because re-render vue component.
             * For epubs you must to use `setEpubLayoutMode`
             */
            state.reader.layout = layout;
        },

        /**
         * UI
         */
        setHeaderbarOffsetHeight(state, payload) {
            state.ui.headerbar.offsetHeight = payload;
        },
        setFooterbarOffsetHeight(state, payload) {
            state.ui.toolbar.offsetHeight = payload;
        },
        /**
         * Scimia EPUB
         */
        filePath(state, payload) {
            state.epub.filePath = payload.filePath;
        },
        setReadium(state, payload) {
            state.readium = payload.readium;
            if (payload.epubViewer) {
                state.epubViewer = payload.epubViewer;
            }
        },
        setEpubViewer(state, payload) {
            state.epubViewer = payload.epubViewer;
        },
        fontSizeUpdate(state, payload) {
            state.settings.fontSize = payload.fontSize;
        },
        setEpubLayoutType(state, payload) {
            state.isFixedLayout = payload.type;
        },
        setEpubLayoutMode(state, payload) {
            state.settings.scroll = payload.scroll;
            state.settings.syntheticSpread = payload.syntheticSpread;
        },
        setDirection(state, payload) {
            state.epub.direction = payload.direction;
        },
        setToc(state, payload) {
            // state.epub.toc = payload.toc;
            state.publication.table_of_contents = payload;
        },
        setHasLoadLastLocation(state) {
            state.hasLoadLastLocation = true;
        },
        setSettingsChange(state, payload) {
            state.settingsChange = payload.status;
        },
        setDoNotTrackFontLoc(state, payload) {
            state.doNotTrackFontLoc = payload.status;
        },
        setContext(state, payload) {
            state.context.push(payload);
        },
        cleanContext(state) {
            state.context = [];
        },
        setUiTheme(state, payload) {
            const theme = payload.theme ? payload.theme : 'light';
            state.ui.theme = theme;
            // Update the themeSelected only if the theme is not 'system'. The ui is listening themeSelected property
            if (theme !== 'system') {
                state.ui.themeSelected = theme;
            }
        },
        setBaseTheme(state, payload) {
            state.ui.baseTheme = payload.theme;
        },
        setThemeSelected(state, payload) {
            state.ui.themeSelected = payload.theme;
        },

        setSettings(state, settings) {
            state.reader.preview = settings.preview;
            state.reader.layout = settings.layout || 'scroll';
            if (settings.pageViewConfirmDelayMs != null) {
                state.reader.pageViewConfirmDelayMs = settings.pageViewConfirmDelayMs;
            }
        },
        setNetworkStatus(state, payload) {
            state.isOffline = payload.isOffline || false;
        },

        //AudioBook
        toggleAudiobookAbout(state) {
            state.ui.audiobook.about.opened = !state.ui.audiobook.about.opened;
        },
        toogleAudiobookPlaylist(state) {
            state.ui.audiobook.playlist.opened = !state.ui.audiobook.playlist.opened;
        },
        loadingAudiobookPlaylist(state, payload) {
            state.audiobook.playlist = payload.playlist;
            state.audiobook.duration = payload.duration;
            state.audiobook.chapters = payload.chapters;
        },
        changeAudiobookSpeed(state, payload) {
            state.audiobook.speed = payload.speed;
        },
        setTwitterUser(state, payload) {
            state.issue.twitterUser = payload.twitterUser;
        },
        openSocialShare(state, payload) {
            state.ui.socialShare.opened = true;
            if (payload) {
                state.ui.socialShare.shareArticle = payload.shareArticle ? payload.shareArticle : false;
                state.ui.socialShare.currentArticleId = payload.currentArticleId ? payload.currentArticleId : null;
            }
        },
        closeSocialShare(state) {
            state.ui.socialShare.opened = false;
            state.ui.socialShare.shareArticle = false;
            state.ui.socialShare.currentArticleId = null;
        },
        openNotesModal(state) {
            state.ui.notesModal.opened = true;
        },
        closeNotesModal(state) {
            state.ui.notesModal.opened = false;
        },
        openConfirmationModal(state, payload) {
            state.ui.confirmationModal.opened = true;
            state.ui.confirmationModal.title = payload.title || '';
            state.ui.confirmationModal.message = payload.message || '';
            state.ui.confirmationModal.confirmLabel = payload.confirmLabel || '';
            state.ui.confirmationModal.cancelLabel = payload.cancelLabel || '';
            state.ui.confirmationModal.onConfirmAction = payload.onConfirmAction || null;
            state.ui.confirmationModal.context = payload.context || {};
        },
        closeConfirmationModal(state) {
            state.ui.confirmationModal.opened = false;
            state.ui.confirmationModal.title = '';
            state.ui.confirmationModal.message = '';
            state.ui.confirmationModal.confirmLabel = '';
            state.ui.confirmationModal.cancelLabel = '';
            state.ui.confirmationModal.onConfirmAction = null;
            state.ui.confirmationModal.context = {};
        },
        setUuidToListen(state, payload) {
            if (payload && payload.uuidToListen) {
                state.ui.listening.uuidToListen = payload.uuidToListen;
            } else {
                state.ui.listening.uuidToListen = '';
            }
        },
        setNotesEnabled(state, payload) {
            state.ui.features.notes.enabled = payload.notes_enabled;
        },
        setReaderPluginSrc(state, payload) {
            state.ui.plugin.src = payload.src;
        },
        setIsBuyable(state, payload) {
            state.isBuyable = payload.isBuyable;
        },
        setUserIsGuest(state, payload) {
            state.userIsGuest = payload.userIsGuest;
        },
        setIsBuyableButtonText(state, payload) {
            state.isBuyableButtonText = payload.isBuyableButtonText;
        },
        setPromoteAppData(state, payload) {
            state.promoteApp.enabled = payload?.enabled || false;
            state.promoteApp.androidAppLink = payload?.android_app_link;
            state.promoteApp.appleAppLink = payload?.apple_app_link;
            state.promoteApp.appName = payload?.app_name;
            state.promoteApp.appIcon = payload?.app_icon;
        },
        setSpineAndTotalWords(state, payload) {
            state.epub.spine = payload.spine;
            state.epub.totalWords = payload.totalWords;
        },
        setSubSpine(state, payload) {
            state.epub.subSpine = payload.subSpine;
        },
        setExhibitionMode(state, payload) {
            if (!payload) return;
            state.exhibitionMode = payload.exhibitionMode;
        },
        toggleShowAddToLists(state) {
            state.addToLists.show = !state.addToLists.show;
        },
        setSessionExpired(state) {
            state.sessionExpired = true;
        },
        setComponentListsIframeUrl(state, payload) {
            state.addToLists.iframeUrl = payload.iframeUrl;
        },
        aiActions(state, payload) {
            state.aiActions = payload;
        },
        setSelectionInProgress(state, payload) {
            state.selectionInProgress = payload.selectionInProgress;
        },
        setFirstPageReached(state, payload) {
            state.reader.pagination.isFirstPage = payload;
        },
        setLastPageReached(state, payload) {
            state.reader.pagination.isLastPage = payload;
        },
    },
    modules: {
        PdfPagedReaderScale,
    },
};
